import React, { useState } from "react";
import { t } from "i18next";
import { colors, fonts, frames } from "../../Styles";
import { Consumption } from "../../Services";
import { LineSegment, VictoryAxis, VictoryBar, VictoryChart, VictoryContainer, VictoryGroup, VictoryLabel, VictoryTooltip } from "victory";

const _feedCurveWidth = 600;
const _chartWidth = 550;
const _chartHeight = 300;
const _fontSize = { ...fonts.fontSize4 };
const _primaryColor = colors.greyPrimary;

// #region | Summed data if more animals
function combineConsumptions(arrays: Consumption[][]) {
  const combinedMap: Record<number, { progressed: number; total: number }> = {};

  for (const arr of arrays) {
    for (const c of arr) {
      const offset = c.milestoneDayOffset;
      if (offset === undefined) continue;

      if (!combinedMap[offset]) {
        combinedMap[offset] = { progressed: 0, total: 0 };
      }

      combinedMap[offset].progressed += c.progressed ?? 0;
      combinedMap[offset].total += c.total ?? 0;
    }
  }
  const result: Consumption[] = [];
  for (let i = 0; i <= 10; i++) {
    result.push({
      milestoneDayOffset: i,
      progressed: combinedMap[i]?.progressed ?? 0,
      total: combinedMap[i]?.total ?? 0,
    });
  }

  return result;
}
// #endregion

interface Last10DaysViewer_Props {
  data?: Consumption[] | Consumption[][];
}

export default function Last10DaysViewer({ data }: Last10DaysViewer_Props) {
  const [hoveredLabel, setHoveredLabel] = useState<number | null>(null);

  // #region | Data
  const consumptionData: Consumption[] = Array.isArray(data?.[0])
    ? combineConsumptions(data as Consumption[][])
    : (data as Consumption[]) ?? [];  
  
  const today = new Date();

  const last10Days = Array.from({ length: 10 }, (_, i) => {
    const found = consumptionData.find(c => c.milestoneDayOffset === i);
    return found ?? {
      milestoneDayOffset: i,
      progressed: 0,
      total: 0,
    };
  });

  const consumptionDataWithDates = last10Days.map(d => ({
    ...d,
    date: new Date(today.getFullYear(), today.getMonth(), today.getDate() - d.milestoneDayOffset!)
  }));

  const maxValue = Math.max(...last10Days.map((d) => d.total ?? 0));

  const feedTotalRange = [0, maxValue / 1000];

  const ticks = 6;

  const tickFormatFeed = (range: number[]) => (t: number) => {
    const value = range[0] + t * ((range[1] - range[0]) / ticks);
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? rounded : rounded.toFixed(2).replace(/0$/, "");
  };
  // #endregion

  // #region | Graph Width
  // To achieve this, I had to reset svg overflow to "visible" in reset.css // MWE
  const longestNumber = feedTotalRange.reduce((maxNumber, currentNumber) => {
    return currentNumber.toString().length > maxNumber.toString().length ? currentNumber : maxNumber;
  }, feedTotalRange[0]);

  const longestNumberLenght = Number(longestNumber.toString().replace(/[.,]/g, '').length)

  const _paddingLeft = longestNumberLenght <= 2 ? 0 : (longestNumberLenght - 2) * 8;
  // #endregion

  // #region | Tooltip
  const tooltip = (
    <VictoryTooltip
      dy={-3}
      cornerRadius={8}
      pointerLength={6}
      pointerWidth={12}
      flyoutPadding={{ top: 1, bottom: 1, left: 6, right: 6 }}
      flyoutStyle={{
        fill: colors.greyDarkPrimary,
        stroke: 0
      }}
      style={{
        fill: colors.greyLightTertiary,
        fontSize: "11px",
        ...fonts.paragraph,
      }}
    />
  )
  // #endregion

  // #region | Bars
  const feedTotalBars = (
    <VictoryBar
      data={consumptionDataWithDates}
      x="date"
      y={(d) => (d.total ?? 0) / 1000}
      style={{
        data: {
          width: 10,
          fill: hoveredLabel === 3 ? colors.greyTertiary : colors.graphBlueFour,
          transition: "0.3s",
        },
      }}
      labels={({ datum }) => `${t("feed.general.expected", { defaultValue: "Expected" })}: ${(datum.total/1000).toFixed(2)} kg`}
      labelComponent={tooltip}
    />
  )

  const feedConsumptionBars = (
    <VictoryBar
      data={consumptionDataWithDates}
      x="date"
      y={(d) => (d.progressed ?? 0) / 1000}
      style={{
        data: {
          width: 10,
          fill: hoveredLabel === 2 ? colors.greyTertiary : colors.graphBlueOne,
          transition: "0.3s",
          
        },
      }}
      labels={({ datum }) => `${t("feed.general.actually", { defaultValue: "Consumed" })}: ${(datum.progressed/1000).toFixed(2)} kg`}
      labelComponent={tooltip}
    />
  )
  // #endregion

  // #region | Axes
  // Y-Axis
  const yAxis = (
    <VictoryAxis
      offsetX={32}
      dependentAxis
      standalone={false}
      tickFormat={tickFormatFeed(feedTotalRange)}
      style={{
        ticks: { stroke: _primaryColor, size: 5 },
        axis: { stroke: _primaryColor, strokeWidth: 2 },
      }}
      crossAxis={false}
      label={t("units.kg", { defaultValue: "KG" })}
      tickLabelComponent={
        <VictoryLabel
          textAnchor="end"
          text={ ({ datum }) => tickFormatFeed(feedTotalRange)(datum)}
          style={{ ...fonts.paragraph, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisLabelComponent={
        <VictoryLabel
          angle={0}
          verticalAnchor="start" 
          textAnchor="start"
          x={0}
          y={0}
          style={{ ...fonts.header, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisComponent={
        <LineSegment
          y1={0}
          y2={_chartHeight - 20}
        />
      }
    />
  )

  // X-Axis
  const xAxis = (
    <VictoryAxis
      offsetY={25}
      orientation="bottom"
      tickFormat={(t: Date) => `${t.getDate()}/${t.getMonth() + 1}`}
      standalone={false}
      tickCount={10}
      style={{
        ticks: { stroke: _primaryColor, size: 5 },
        axis: { stroke: _primaryColor, strokeWidth: 2 },
      }}
      crossAxis={false}
      label={t("units.date", {defaultValue: "Date"})}
      tickLabelComponent={
        <VictoryLabel
          y={_chartHeight}
          verticalAnchor="end"
          style={{ ...fonts.paragraph, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisLabelComponent={
        <VictoryLabel
          y={_chartHeight}
          x={_chartWidth}
          verticalAnchor="end"
          textAnchor="end"
          style={{ ...fonts.header, fill: _primaryColor, ..._fontSize }}
        />
      }
      axisComponent={ <LineSegment x1={27} x2={_chartWidth} /> }
    />
  )
  // #endregion

  return (
    <div style={{ width: _feedCurveWidth, display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", paddingLeft: _paddingLeft}}>
      
      <VictoryChart
        padding={{ top: 40, bottom: 25, left: 45, right: 40 }}
        width={_chartWidth}
        height={_chartHeight}
        scale={{ x: "time" }}
        // style={{ parent: { pointerEvents: "auto", userSelect: "auto", touchAction: "auto" } }}
      >
        <VictoryGroup offset={12}>
          {feedTotalBars}
          {feedConsumptionBars}
        </VictoryGroup>
        {yAxis}
        {xAxis}
      </VictoryChart>
            
      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        <frames.Label
          $component={3}
          $isHovered={hoveredLabel === 3}
          onMouseEnter={() => setHoveredLabel(3)}
          onMouseLeave={() => setHoveredLabel(null)}
        >
          {t("feed.general.expected", { defaultValue: "expected consumption" })}
        </frames.Label>
        <frames.Label
          $component={2}
          $isHovered={hoveredLabel === 2}
          onMouseEnter={() => setHoveredLabel(2)}
          onMouseLeave={() => setHoveredLabel(null)}
        >
          {t("feed.general.actually", {defaultValue: "actually consumed"})}
        </frames.Label>
      </div>
      
    </div>
  );
}